/**
 * Braze Web SDK singleton.
 *
 * Consent / policy: see docs/context/FEATURE_SPECS/braze-web-sdk.md (REK-1344).
 *
 * Login-gated (not cookie-consent-gated):
 *   - Braze initializes on every page load via main.tsx.
 *   - User identification happens in AuthProvider when the user logs in.
 *   - Cookie consent only controls PostHog and GA4/GTM, not Braze.
 *
 * Call order for authenticated sessions:
 *   1. initBraze()              — main.tsx, on every page load
 *   2. brazeChangeUser(userId)  — AuthProvider, on login
 *   3. brazeOpenSession()       — AuthProvider, immediately after brazeChangeUser()
 */

import * as braze from '@braze/web-sdk';
import { getAnalyticsConfig } from './config';
import { safeDebug, safeLog } from '@/lib/safeLog';

let _initialized = false;
let _currentUserId: string | null = null;

export function initBraze(): void {
  if (_initialized) return;

  const config = getAnalyticsConfig();
  if (!config.brazeSdkApiKey || !config.brazeSdkEndpoint) {
    safeLog('[Braze] Missing API key or SDK endpoint — skipping init');
    return;
  }

  braze.initialize(config.brazeSdkApiKey, {
    baseUrl: config.brazeSdkEndpoint,
    enableLogging: config.environment === 'development',
    doNotLoadFontAwesome: true,
  });

  braze.automaticallyShowInAppMessages();

  _initialized = true;
  safeDebug('[Braze] Initialized');
}

export function brazeOpenSession(): void {
  if (!_initialized) return;
  braze.openSession();
}

export function brazeChangeUser(userId: string): void {
  if (!_initialized) return;
  if (_currentUserId === userId) return;
  braze.changeUser(userId);
  _currentUserId = userId;
  safeDebug('[Braze] changeUser:', userId);
}

export function brazeLogEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean>
): void {
  if (!_initialized) return;
  braze.logCustomEvent(eventName, properties);
}

export function brazeSetAttribute(
  key: string,
  value: string | number | boolean | Date | string[] | null
): void {
  if (!_initialized) return;
  const user = braze.getUser();
  if (!user) return;
  user.setCustomUserAttribute(key, value as string | number | boolean | Date | string[] | null);
}

export function brazeSetEmail(email: string): void {
  if (!_initialized) return;
  const user = braze.getUser();
  if (!user) return;
  user.setEmail(email);
}

// Native Braze user fields. Prefer these over custom attributes for the
// reserved fields Braze ships out-of-the-box (first_name, last_name, phone,
// country) so Liquid personalization (e.g. `{{${first_name}}}`) works without
// extra mapping. Each guards on init + an available user object like the
// setters above.

export function brazeSetFirstName(firstName: string): void {
  if (!_initialized) return;
  const user = braze.getUser();
  if (!user) return;
  user.setFirstName(firstName);
}

export function brazeSetLastName(lastName: string): void {
  if (!_initialized) return;
  const user = braze.getUser();
  if (!user) return;
  user.setLastName(lastName);
}

export function brazeSetPhoneNumber(phoneNumber: string): void {
  if (!_initialized) return;
  const user = braze.getUser();
  if (!user) return;
  user.setPhoneNumber(phoneNumber);
}

export function brazeSetCountry(country: string): void {
  if (!_initialized) return;
  const user = braze.getUser();
  if (!user) return;
  user.setCountry(country);
}

export function brazeWipeData(): void {
  if (_initialized) {
    braze.wipeData();
  }
  _initialized = false;
  _currentUserId = null;
  // Re-initialize so subsequent SPA logins work without a full page reload.
  // wipeData() destroys internal SDK state; we need a fresh initialize() call.
  initBraze();
  safeDebug('[Braze] Data wiped and SDK re-initialized (user signed out)');
}

// Content Cards (REK-1421). Every wrapper below is safe to call at any time:
// it no-ops when the SDK is not initialized and swallows SDK throws so a
// Braze outage never breaks the page. Never call showContentCards and never
// import Braze CSS; cards render in Reklaim's own styling.

export function brazeSubscribeToContentCardsUpdates(
  callback: (cards: braze.ContentCards) => void,
): () => void {
  if (!_initialized) return () => {};
  try {
    const id = braze.subscribeToContentCardsUpdates(callback);
    return () => {
      if (id === undefined) return;
      try {
        braze.removeSubscription(id);
      } catch (error) {
        safeLog('[Braze] removeSubscription failed', error);
      }
    };
  } catch (error) {
    safeLog('[Braze] subscribeToContentCardsUpdates failed', error);
    return () => {};
  }
}

export function brazeRequestContentCardsRefresh(): void {
  if (!_initialized) return;
  try {
    braze.requestContentCardsRefresh(undefined, () => {
      safeDebug('[Braze] content cards refresh failed');
    });
  } catch (error) {
    safeLog('[Braze] requestContentCardsRefresh failed', error);
  }
}

export function brazeGetCachedContentCards(): braze.ContentCards | undefined {
  if (!_initialized) return undefined;
  try {
    return braze.getCachedContentCards();
  } catch (error) {
    safeLog('[Braze] getCachedContentCards failed', error);
    return undefined;
  }
}

export function brazeLogContentCardImpressions(cards: braze.Card[]): void {
  if (!_initialized) return;
  try {
    braze.logContentCardImpressions(cards);
  } catch (error) {
    safeLog('[Braze] logContentCardImpressions failed', error);
  }
}

export function brazeLogContentCardClick(card: braze.Card): void {
  if (!_initialized) return;
  try {
    braze.logContentCardClick(card);
  } catch (error) {
    safeLog('[Braze] logContentCardClick failed', error);
  }
}

export function brazeLogCardDismissal(card: braze.Card): void {
  if (!_initialized) return;
  try {
    braze.logCardDismissal(card);
  } catch (error) {
    safeLog('[Braze] logCardDismissal failed', error);
  }
}
